/**
 * Post Composer — bottom sheet for creating a new post.
 * Text input + image/video upload (up to 4 media via R2).
 */

import { useState, useCallback, useRef, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ImagePlus, Video, Send } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useMediaUpload } from '@turnifly/shared/hooks'
import { useCreatePost } from '@turnifly/shared/hooks'
import { PremiumPage } from '../../profile/components/premium-page'

interface PostComposerProps {
  isOpen: boolean
  onClose: () => void
  onPostCreated?: () => void
}

const MAX_TEXT = 2000
const MAX_MEDIA = 4
const VIDEO_EXTS = ['.mp4', '.mov', '.webm', '.ogg']

function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/') || VIDEO_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext))
}

function isVideoUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0]
  return VIDEO_EXTS.some((ext) => lower.endsWith(ext))
}

export const PostComposer = memo(function PostComposer({
  isOpen,
  onClose,
  onPostCreated,
}: PostComposerProps): React.ReactElement | null {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const userId = user?.id ?? ''
  const isPremium = user?.is_premium === true
  const createMutation = useCreatePost()
  const { upload, isUploading } = useMediaUpload()
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const [content, setContent] = useState('')
  const [showPremium, setShowPremium] = useState(false)
  const [mediaUrls, setMediaUrls] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)

  const canPost = content.trim().length > 0 || mediaUrls.length > 0
  const isSubmitting = createMutation.isPending || uploading
  const hasVideo = mediaUrls.some(isVideoUrl)

  const handleMediaPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = e.target.files
    if (!files) return

    const remaining = MAX_MEDIA - mediaUrls.length
    const toUpload = Array.from(files).slice(0, remaining)
    if (toUpload.length === 0) return

    setUploading(true)
    const urls: string[] = []
    for (const file of toUpload) {
      try {
        const category = isVideoFile(file) ? 'post' : 'post'
        const result = await upload(file, category)
        if (result?.url) urls.push(result.url)
      } catch {
        // Skip failed uploads silently
      }
    }
    setMediaUrls((prev) => [...prev, ...urls])
    setUploading(false)

    // Reset inputs
    if (imageInputRef.current) imageInputRef.current.value = ''
    if (videoInputRef.current) videoInputRef.current.value = ''
  }, [mediaUrls.length, upload])

  const removeMedia = useCallback((index: number): void => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = useCallback((): void => {
    if (!canPost || isSubmitting) return

    const mediaType = hasVideo ? 'video' as const : mediaUrls.length > 0 ? 'image' as const : null

    createMutation.mutate(
      {
        userId,
        content: content.trim() || undefined,
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
        mediaType,
      },
      {
        onSuccess: () => {
          setContent('')
          setMediaUrls([])
          onClose()
          onPostCreated?.()
        },
      },
    )
  }, [canPost, isSubmitting, userId, content, mediaUrls, hasVideo, createMutation, onClose])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--color-bg)] rounded-t-[28px] max-w-lg mx-auto max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-white/[0.12] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3 shrink-0">
          <button onClick={onClose} className="text-[14px] text-text-hint tap-highlight-transparent">
            {t('common.cancel')}
          </button>
          <h3 className="text-[15px] font-semibold text-text">{t('feed.composer.title')}</h3>
          <button
            onClick={handleSubmit}
            disabled={!canPost || isSubmitting}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-semibold transition-all tap-highlight-transparent ${
              canPost && !isSubmitting
                ? 'bg-primary text-white shadow-sm shadow-primary/20'
                : 'bg-white/[0.04] text-text-hint/25'
            }`}
          >
            <Send size={13} />
            {t('feed.composer.post')}
          </button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-5 pb-3">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="shrink-0 w-10 h-10 rounded-full overflow-hidden bg-white/[0.06] ring-1 ring-white/[0.06]">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[15px] font-semibold text-text-hint/50">
                  {(user?.full_name ?? '?')[0]}
                </div>
              )}
            </div>

            {/* Textarea */}
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, MAX_TEXT))}
              placeholder={t('feed.composer.placeholder')}
              rows={5}
              autoFocus
              className="flex-1 bg-transparent text-[15px] text-text placeholder:text-text-hint/25 resize-none focus:outline-none leading-relaxed min-h-[120px]"
            />
          </div>

          {/* Character count */}
          {content.length > MAX_TEXT * 0.8 && (
            <p className={`text-right text-[11px] mt-1 tabular-nums ${
              content.length >= MAX_TEXT ? 'text-red-400' : 'text-text-hint/30'
            }`}>
              {content.length}/{MAX_TEXT}
            </p>
          )}

          {/* Uploaded media grid */}
          {mediaUrls.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              {mediaUrls.map((url, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden aspect-square">
                  {isVideoUrl(url) ? (
                    <video
                      src={url}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  )}
                  <button
                    onClick={() => removeMedia(i)}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm text-white tap-highlight-transparent hover:bg-black/80 transition-colors"
                  >
                    <X size={12} />
                  </button>
                  {isVideoUrl(url) && (
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/50 backdrop-blur-sm text-[10px] text-white/80 font-medium">
                      VIDEO
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Uploading indicator */}
          {(uploading || isUploading) && (
            <div className="flex items-center gap-2 mt-3">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-[12px] text-text-hint/50">{t('common.uploading')}</span>
            </div>
          )}
        </div>

        {/* Bottom toolbar */}
        <div className="flex items-center gap-2 px-5 py-3 border-t border-white/[0.06] shrink-0 pb-safe">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleMediaPick}
            className="hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleMediaPick}
            className="hidden"
          />

          <button
            onClick={() => isPremium ? imageInputRef.current?.click() : setShowPremium(true)}
            disabled={mediaUrls.length >= MAX_MEDIA || uploading}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-medium transition-colors tap-highlight-transparent ${
              mediaUrls.length >= MAX_MEDIA
                ? 'bg-white/[0.02] text-text-hint/15'
                : 'bg-white/[0.04] text-text-hint/60 hover:bg-white/[0.06]'
            }`}
          >
            <ImagePlus size={15} />
            {t('feed.composer.image')}
          </button>

          <button
            onClick={() => isPremium ? videoInputRef.current?.click() : setShowPremium(true)}
            disabled={mediaUrls.length >= MAX_MEDIA || uploading}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12px] font-medium transition-colors tap-highlight-transparent ${
              mediaUrls.length >= MAX_MEDIA
                ? 'bg-white/[0.02] text-text-hint/15'
                : 'bg-white/[0.04] text-text-hint/60 hover:bg-white/[0.06]'
            }`}
          >
            <Video size={15} />
            {t('feed.composer.video')}
          </button>

          {mediaUrls.length > 0 && (
            <span className="ml-auto text-[11px] text-text-hint/30 tabular-nums">
              {mediaUrls.length}/{MAX_MEDIA}
            </span>
          )}
        </div>
      </motion.div>
      <PremiumPage isOpen={showPremium} onClose={() => setShowPremium(false)} />
    </AnimatePresence>
  )
})
